
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface Destination {
  title: string;
  duration: string;
  description: string[];
  image: string;
  buttonText: string;
  buttonAction: string;
}

const destinations: Destination[] = [
  {
    title: "Classic Antarctic Peninsula Expedition (10–12 Days)",
    duration: "Route: Ushuaia – Drake Passage – Antarctic Peninsula – South Shetland Islands – Ushuaia",
    description: [
      "Cross the legendary Drake Passage",
      "Explore Deception Island and Paradise Bay",
      "Zodiac landings with penguins, seals, and whales",
      "Visit research stations and icy fjords",
      "Best For: First-time travelers seeking the quintessential Antarctica experience."
    ],
    image: "/images/antarctica-dest-1.jpg",
    buttonText: "Explore More",
    buttonAction: "explore"
  },
  {
    title: "Fly & Cruise Antarctica Express (6-8 Days)",
    duration: "Route: Punta Arenas (Chile) – King George Island (fly) – Antarctic Peninsula – Punta Arenas (fly)",
    description: [
      "Skip the Drake Passage (fly both ways)",
      "Short expedition ideal for limited time",
      "Daily shore excursions & zodiac cruises",
      "Best For: Luxury travelers or those prone to seasickness."
    ],
    image: "/images/antarctica-dest-2.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "South Georgia & Antarctic Peninsula Grand Voyage (18-21 Days)",
    duration: "Route: Ushuaia - Falkland Islands - South Georgia - Antarctic Peninsula - Ushuaia",
    description: [
      "Visit penguin colonies up to 500,000 strong",
      "Follow Shackleton's legendary route",
      "Incredible wildlife: elephant seals, albatrosses, and king penguins",
      "Best For: Wildlife enthusiasts and photographers."
    ],
    image: "/images/antarctica-dest-3.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Weddell Sea Icebreaker Expedition (12-14 Days)",
    duration: "Route: Ushuaia - Antarctic Sound - Weddell Sea - Larsen Ice Shelf - Ushuaia",
    description: [
      "Navigate through thick pack ice aboard an ice-strengthened vessel",
      "Chance to see emperor penguins and tabular icebergs",
      "Visit remote east-coast landing sites",
      "Best For: Adventure seekers and polar explorers."
    ],
    image: "/images/antarctica-dest-4.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Polar Circle Quest (14–16 Days)",
    duration: "Route: Ushuaia – Antarctic Peninsula – Crossing the Antarctic Circle – Ushuaia",
    description: [
      "Cross 66°33′ South — the Antarctic Circle​",
      "Explore rarely visited Marguerite Bay​",
      "Iceberg alley and deep south landings​",
      "Best For: Experienced travelers looking for deeper exploration."
    ],
    image: "/images/antarctica-dest-5.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Luxury Antarctica & South Pole Experience (10–15 Days)",
    duration: "Route: Cape Town – Wolf’s Fang Runway (fly) – South Pole Camp – Emperor Penguin Colony – Cape Town",
    description: [
      "Private jet flights and luxury ice camps",
      "Visit the Geographic South Pole",
      "Meet emperor penguins in Atka Bay",
      "Best For: Ultra-luxury travelers seeking bucket-list experiences."
    ],
    image: "/images/antarctica-dest-6.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Adventure Antarctica: Kayak, Hike & Camp (12–13 Days)",
    duration: "Route: Ushuaia – Antarctic Peninsula – South Shetland Islands – Ushuaia",
    description: [
      "Overnight camping on the ice",
      "Kayaking between icebergs and wildlife",
      "Guided hikes and glacier climbs",
      "Best For: Active travelers and photographers."
    ],
    image: "/images/antarctica-dest-7.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Falklands, South Georgia & Snow Hill Emperor Penguin Voyage (20-23 Days)",
    duration: "Route: Ushuaia – Falklands – South Georgia – Weddell Sea – Snow Hill Island – Ushuaia",
    description: [
      "Rare access to emperor penguin rookery",
      "Expert-led wildlife and photography lectures",
      "Helicopter transfers to remote sites",
      "Best For: Wildlife documentarians and serious explorers."
    ],
    image: "/images/antarctica-dest-8.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Antarctic Solar Eclipse & Astronomy Expedition (Special Departure)",
    duration: "Route: Ushuaia – Weddell Sea – Scotia Sea – Antarctic Peninsula – Ushuaia",
    description: [
      "Witness a solar eclipse from the polar region",
      "Astronomy lectures and night-sky viewing",
      "Rare celestial event combined with polar cruising",
      "Best For: Science lovers and once-in-a-lifetime travelers."
    ],
    image: "/images/antarctica-dest-9.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  },
  {
    title: "Antarctic Circle & Patagonia Explorer (17-19 Days)",
    duration: "Route: Ushuaia - Antarctic Peninsula - Antarctic Circle - Cape Horn - Torres del Paine (Chile)",
    description: [
      "Combine Antarctic wilderness with Patagonia's national parks",
      "Zodiac cruising, hiking, and scenic fjords",
      "Best For: Travelers wanting both poles of beauty — ice and mountains."
    ],
    image: "/images/antarctica-dest-10.jpg",
    buttonText: "Enquire Now",
    buttonAction: "enquire"
  }
];

export const DestinationsSection: React.FC = () => {
  const navigate = useNavigate();

  const handleButtonClick = (action: string, title: string) => {
    if (action === 'enquire') {
      navigate(`/contact?subject=${encodeURIComponent('Enquiry about ' + title)}`);
      return;
    }

    if (action === 'explore') {
      navigate('/group-tours');
      return;
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">

          </h2>
        </div>

        <div className="space-y-16">
          {destinations.map((destination, index) => (
            <div
              key={index}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
            >
              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
                  {destination.title}
                </h3>
                <p className="text-lg text-emerald-600 font-medium">
                  {destination.duration}
                </p>
                <ul className="space-y-3">
                  {destination.description.map((desc, descIndex) => (
                    <li key={descIndex} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-gray-700 leading-relaxed">{desc}</p>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleButtonClick(destination.buttonAction, destination.title)}
                  className="inline-flex items-center px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  {destination.buttonText}
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center ml-2"></i>
                </button>
              </div>

              <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                <img
                  src={destination.image}
                  alt={destination.title}
                  className="w-full h-80 object-cover object-center rounded-lg shadow-lg"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
